import Uppy from '@uppy/core';
import exifr from 'exifr';

export interface PhotoExif {
    latitude?: number;
    longitude?: number;
    direction?: number;
    DateTimeOriginal?: string | Date;
    CreateDate?: string | Date;
    GPSAltitude?: number;
    Make?: string;
    Model?: string;
}

const HEIC_EXTENSION_RE = /\.(heic|heif)$/i;

const EXIF_PICK = ['DateTimeOriginal', 'CreateDate', 'latitude', 'longitude', 'GPSLatitude', 'GPSLongitude', 'GPSImgDirection', 'GPSDestBearing', 'GPSAltitude', 'Make', 'Model'];

export function isHeicFilename(name: string): boolean {
    return HEIC_EXTENSION_RE.test(name ?? '');
}

function computeDirection(output: any): number | undefined {
    const direction =
        (output.GPSImgDirection == 0 ? null : output.GPSImgDirection) ??
        (output.GPSDestBearing == 0 ? null : output.GPSDestBearing) ??
        output.FlightYawDegree ??
        output.GimbalYawDegree;
    if (direction == null) {
        return undefined;
    }
    return ((direction % 360) + 360) % 360;
}

// Browsers (except Safari) can't decode HEIC and the backend can't extract its EXIF or
// generate jpeg thumbnails from it, so photos must reach the server as JPEG with the
// coordinates passed along as upload parameters.
export async function extractPhotoExif(data: Blob): Promise<PhotoExif | null> {
    let output = null;
    try {
        output = await exifr.parse(data, {
            exif: { pick: EXIF_PICK },
            xmp: { pick: ['FlightYawDegree', 'GimbalYawDegree'] },
        });
    } catch {
        // fall through to the container scan below
    }
    if (!output || Object.keys(output).length === 0) {
        output = await extractExifFromHeicContainer(data);
    }
    if (!output || Object.keys(output).length === 0) {
        return null;
    }
    return { ...output, direction: computeDirection(output) };
}

/**
 * exifr does not recognize some newer HEIC containers (e.g. iOS 18 HDR gain-map photos,
 * "Unknown file format"), but it parses a raw TIFF/EXIF block fine. Locate the EXIF payload
 * inside the ISO-BMFF container by its "Exif\0\0" marker followed by a TIFF header and hand
 * exifr that buffer directly.
 */
async function extractExifFromHeicContainer(data: Blob): Promise<any | null> {
    try {
        const bytes = new Uint8Array(await data.arrayBuffer());
        for (let i = 0; i < bytes.length - 10; i++) {
            // "Exif\0\0"
            if (bytes[i] !== 0x45 || bytes[i + 1] !== 0x78 || bytes[i + 2] !== 0x69 || bytes[i + 3] !== 0x66 || bytes[i + 4] !== 0 || bytes[i + 5] !== 0) {
                continue;
            }
            const t = i + 6;
            // TIFF magic: "II*\0" (little endian) or "MM\0*" (big endian)
            const isTiff =
                (bytes[t] === 0x49 && bytes[t + 1] === 0x49 && bytes[t + 2] === 0x2a && bytes[t + 3] === 0x00) ||
                (bytes[t] === 0x4d && bytes[t + 1] === 0x4d && bytes[t + 2] === 0x00 && bytes[t + 3] === 0x2a);
            if (!isTiff) {
                continue;
            }
            try {
                const output = await exifr.parse(bytes.subarray(t), { tiff: true, gps: true, exif: { pick: EXIF_PICK } });
                if (output && Object.keys(output).length > 0) {
                    return output;
                }
            } catch {
                // try the next marker occurrence
            }
        }
    } catch {}
    return null;
}

export async function convertHeicToJpegBlob(data: Blob): Promise<Blob> {
    // lazy import: the libheif wasm decoder is ~1.4MB, only load it when a heic file is actually uploaded.
    // The wasm-bundle variant is fully self-contained (no runtime .wasm fetch), which webpack requires.
    const libheif = await import('libheif-js/wasm-bundle');
    const decoder = new libheif.HeifDecoder();
    const images = decoder.decode(await data.arrayBuffer());
    if (!images || images.length === 0) {
        throw new Error('libheif found no images in HEIC file');
    }
    const image = images[0];
    const width = image.get_width();
    const height = image.get_height();

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    const imageData = context.createImageData(width, height);
    await new Promise<void>((resolve, reject) => {
        image.display(imageData, displayData => {
            if (!displayData) {
                reject(new Error('libheif failed to decode HEIC image'));
            } else {
                resolve();
            }
        });
    });
    context.putImageData(imageData, 0, 0);

    const blob = await new Promise<Blob>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.92));
    if (!blob) {
        throw new Error('canvas.toBlob failed for decoded HEIC image');
    }
    return blob;
}

async function convertUppyFileToJpeg(uppy: Uppy, fileID: string): Promise<boolean> {
    const file = uppy.getFile(fileID);
    if (!file || !isHeicFilename(file.name)) {
        return false;
    }
    try {
        const photoExif = await extractPhotoExif(file.data);
        const jpegBlob = await convertHeicToJpegBlob(file.data);
        const newName = file.name.replace(HEIC_EXTENSION_RE, '.jpg');
        const newFile = new File([jpegBlob], newName, { type: 'image/jpeg' });
        uppy.setFileState(fileID, {
            data: newFile,
            size: newFile.size,
            type: 'image/jpeg',
            name: newName,
            meta: { ...file.meta, name: newName, type: 'image/jpeg', photoExif, convertedFromHeic: file.name },
        });
        // the thumbnail generated at add time was for the undecodable heic — request a new one for the jpeg
        uppy.emit('thumbnail:request', uppy.getFile(fileID));
        return true;
    } catch (e) {
        // upload the original file, it just won't be geolocated or viewable outside Safari
        console.error(`HEIC to JPEG conversion failed for ${file.name}, uploading original file`, e);
        return false;
    }
}

/**
 * Converts .heic/.heif files to JPEG as soon as they are added to the upload queue, so the
 * user sees the converted .jpg name in the dialog before uploading. The upload preprocessor
 * only awaits conversions still in flight (or retries one that has not run).
 * The decoded JPEG has no EXIF, so the original's metadata is stored in file.meta.photoExif —
 * getS3Url implementations must pass lat/lon/direction/originalTimestamp from it as upload params.
 * meta.convertedFromHeic holds the original filename for labeling in the UI.
 */
export function registerHeicConversion(uppy: Uppy, onFileConverted?: (file: ReturnType<Uppy['getFile']>) => void): void {
    const pending = new Map<string, Promise<boolean>>();

    const convert = (fileID: string): Promise<boolean> => {
        const task = convertUppyFileToJpeg(uppy, fileID)
            .then(converted => {
                if (converted && onFileConverted) {
                    onFileConverted(uppy.getFile(fileID));
                }
                return converted;
            })
            .finally(() => pending.delete(fileID));
        pending.set(fileID, task);
        return task;
    };

    uppy.on('file-added', file => {
        if (isHeicFilename(file.name)) {
            convert(file.id);
        }
    });

    uppy.addPreProcessor(async (fileIDs: string[]) => {
        for (const fileID of fileIDs) {
            const file = uppy.getFile(fileID);
            if (!file) {
                continue;
            }
            const task = pending.get(fileID) ?? (isHeicFilename(file.name) ? convert(fileID) : null);
            if (!task) {
                continue;
            }
            uppy.emit('preprocess-progress', file, { mode: 'indeterminate', message: 'Converting…' });
            try {
                await task;
            } finally {
                uppy.emit('preprocess-complete', uppy.getFile(fileID));
            }
        }
    });
}
